import { useState, type ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Typography, type SxProps, type Theme } from '@mui/material';
import { SectionCard } from '@kangaroo/ui';

type CollapsibleSectionCardProps = {
  title: string;
  subtitle?: string;
  defaultExpanded?: boolean;
  action?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
};

export function CollapsibleSectionCard({
  title,
  subtitle,
  defaultExpanded = false,
  action,
  children,
  sx,
  contentSx,
}: CollapsibleSectionCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  function toggleExpanded() {
    setExpanded((value) => !value);
  }

  return (
    <SectionCard
      sx={sx}
      contentSx={{ p: 0, '&:last-child': { pb: 0 } }}
    >
      <Box
        onClick={toggleExpanded}
        sx={{
          px: 2.5,
          pt: 2,
          pb: expanded ? 1 : 2,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 28 }}>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: '1rem', fontWeight: 800, lineHeight: '28px' }}>
            {title}
          </Typography>
          <Box
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 28, gap: 0.25, flexShrink: 0 }}
            onClick={(event) => event.stopPropagation()}
          >
            {action}
            <IconButton
              size="small"
              onClick={toggleExpanded}
              aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
              aria-expanded={expanded}
              sx={{ width: 28, height: 28, p: 0, m: 0 }}
            >
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms ease',
                }}
              />
            </IconButton>
          </Box>
        </Box>
        {subtitle && expanded ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45, pr: 4 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            px: 2.5,
            pb: 2.5,
            ...contentSx,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {children}
        </Box>
      </Collapse>
    </SectionCard>
  );
}
